import { createContext } from 'react'

// One microphone and one AudioContext feed the canvases, peak meter, and
// needles. A Dock web app mishandles several captures of the same device.
//
// Each widget still owns its Analysers. getByteFrequencyData applies
// smoothing on every read, so sharing one node across the canvases, LEDs,
// and needles would stack the FAST/CLASSIC constant four times per frame.
export type SharedInput = {
  context: AudioContext
  source: MediaStreamAudioSourceNode
  deliveredChannels: number
  splitter: ChannelSplitterNode | null
  scaleRef: { current: number }
  fastRef: { current: boolean }
  subscribeFrame: (cb: () => void) => () => void
  tick: () => void
  retain: () => void
  release: () => void
}

// undefined: this screen opens its own input.
// null: the deck owns the input and it is not ready yet.
export const SharedInputContext = createContext<SharedInput | null | undefined>(
  undefined
)

const resumeCleanups = new WeakMap<AudioContext, () => void>()

// Safari often leaves a context suspended after getUserMedia, and a Dock
// app suspends it again when the window is hidden.
export const resumeAudioContext = (context: AudioContext) => {
  const kick = () => {
    if (context.state === 'suspended') {
      context.resume().catch(() => {})
    }
  }
  kick()
  if (resumeCleanups.has(context)) return

  const onGesture = () => kick()
  const onVisible = () => {
    if (document.visibilityState === 'visible') kick()
  }
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('pointerdown', onGesture)
  window.addEventListener('keydown', onGesture)
  resumeCleanups.set(context, () => {
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('pointerdown', onGesture)
    window.removeEventListener('keydown', onGesture)
  })
}

export const releaseAudioContextResume = (context: AudioContext) => {
  resumeCleanups.get(context)?.()
  resumeCleanups.delete(context)
}

export const holdInput = (
  context: AudioContext,
  stream: MediaStream,
  source: MediaStreamAudioSourceNode,
  deliveredChannels: number,
  options: {
    splitter?: ChannelSplitterNode | null
    scaleRef: { current: number }
    fastRef: { current: boolean }
  }
): SharedInput => {
  let holders = 0
  let closed = false
  const listeners = new Set<() => void>()

  const finish = () => {
    if (closed) return
    closed = true
    listeners.clear()
    releaseAudioContextResume(context)
    stream.getTracks().forEach((track) => track.stop())
    if (context.state !== 'closed') {
      context.close().catch(() => {})
    }
  }

  return {
    context,
    source,
    deliveredChannels,
    splitter: options.splitter ?? null,
    scaleRef: options.scaleRef,
    fastRef: options.fastRef,
    subscribeFrame(cb) {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
    tick() {
      listeners.forEach((cb) => cb())
    },
    retain() {
      if (!closed) holders += 1
    },
    release() {
      if (closed) return
      holders -= 1
      if (holders <= 0) finish()
    },
  }
}
